import {
  ArrowLeft,
  Clock,
  Lock,
  MapPin,
  Phone as PhoneIcon,
  Settings,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ChatPanel } from "@/components/groups/chat-panel";
import { DayChips, DayPicker } from "@/components/groups/day-chips";
import { DayStatusBanner } from "@/components/groups/day-status-banner";
import { PendingRequestCard } from "@/components/groups/pending-request-card";
import { UserAvatar } from "@/components/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useGroup, useLeaveGroup, useMyGroups, useRequestJoin } from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import { formatDays, formatMonthYear, formatTime } from "@/lib/format";
import { myDays } from "@/lib/groups";

/** Group detail: join flow for seekers, chat + full info for the crew. */
export function GroupPage() {
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: group, isLoading, isError } = useGroup(groupId);
  const { data: myGroups } = useMyGroups();
  const requestJoin = useRequestJoin(groupId);
  const leaveGroup = useLeaveGroup();

  const [joinOpen, setJoinOpen] = useState(false);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinDays, setJoinDays] = useState<number[] | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }
  if (!group) {
    // A failed fetch or a group that no longer exists used to render an
    // empty page — no message, no way back.
    return (
      <div className="flex flex-col gap-4">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground -ml-2 self-start">
          <Link to="/">
            <ArrowLeft aria-hidden /> {t.common.back}
          </Link>
        </Button>
        <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
          {isError ? t.common.loadError : t.group.notFound}
        </p>
      </div>
    );
  }

  const isDriver = user?.id === group.driver.id;
  const isCrew = isDriver || group.my_membership_status === "active";
  const isPending = group.my_membership_status === "requested";

  // Days already spoken for elsewhere: the car I drive, and seats I hold or
  // have asked for. One person never drives and rides on the same weekday;
  // the API enforces it, this just keeps those days out of the picker.
  const busyDays = new Set(
    (myGroups ?? [])
      .filter((other) => other.id !== group.id)
      .flatMap((other) => myDays(other, user?.id)),
  );
  // ...and days where the car is already full. The card's badge is the best
  // day, so a car full Mon–Thu still advertises its Friday seat — the picker
  // must not pre-select the four days the API will refuse.
  const freeByDay = new Map(group.day_availability.map((entry) => [entry.day, entry.free]));
  const joinableDays = group.days.filter(
    (day) => !busyDays.has(day) && (freeByDay.get(day) ?? 0) > 0,
  );
  const canJoin =
    !isCrew &&
    !isPending &&
    group.seats_free > 0 &&
    group.status === "active" &&
    joinableDays.length > 0;

  return (
    <div className="animate-in fade-in flex flex-col gap-5 duration-300">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground -ml-2">
          <Link to="/">
            <ArrowLeft aria-hidden /> {t.common.back}
          </Link>
        </Button>
        {isDriver && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/grupy/${group.id}/edytuj`}>
              <Settings aria-hidden /> {t.group.settings}
            </Link>
          </Button>
        )}
      </div>

      <header className="flex items-center gap-4">
        <UserAvatar user={group.driver} className="size-14" fallbackClassName="bg-primary text-primary-foreground text-lg font-bold" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold">{group.driver.full_name}</h1>
          <p className="text-muted-foreground text-sm">
            {t.group.driver} · {group.car_model}
          </p>
        </div>
        <Badge variant={group.seats_free > 0 ? "default" : "secondary"}>
          {t.group.seatsFree(group.seats_free)}
        </Badge>
      </header>

      {group.today_status && <DayStatusBanner status={group.today_status} />}

      <div className="bg-card flex flex-col gap-3 rounded-xl border p-4">
        <p className="flex items-center gap-2 text-sm">
          <MapPin className="text-primary size-4 shrink-0" aria-hidden />
          <span className="font-medium">
            {group.pickup_area.name}
            <span className="text-muted-foreground font-normal">
              {" "}
              → {group.plant.name} ({group.plant.address})
            </span>
          </span>
        </p>
        {isCrew && group.pickup_details ? (
          <p className="text-muted-foreground pl-6 text-sm">{group.pickup_details}</p>
        ) : (
          !isCrew && (
            <p className="text-muted-foreground flex items-center gap-2 pl-6 text-xs">
              <Lock className="size-3" aria-hidden /> {t.group.exactPickupHidden}
            </p>
          )
        )}
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-3 text-sm">
          <span className="flex items-center gap-1.5 tabular-nums">
            <Clock className="size-4" aria-hidden /> {t.group.departure}{" "}
            <strong className="text-foreground">{formatTime(group.departure_time)}</strong>
          </span>
          <span className="tabular-nums">
            {t.group.return}{" "}
            <strong className="text-foreground">{formatTime(group.return_time)}</strong>
          </span>
          <DayChips
            days={group.days}
            availability={Object.fromEntries(
              group.day_availability.map((entry) => [entry.day, entry.free]),
            )}
          />
        </div>
        {isCrew && group.driver_phone && (
          <a
            href={`tel:${group.driver_phone.replace(/\s/g, "")}`}
            className="text-muted-foreground flex items-center gap-1.5 text-sm underline-offset-2 hover:underline"
          >
            <PhoneIcon className="size-3.5" aria-hidden /> {t.group.driver}:{" "}
            {group.driver_phone}
          </a>
        )}
      </div>

      {(isCrew || group.members.length > 0) && (
      <section>
        <h2 className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
          <Users className="size-3.5" aria-hidden />
          {t.group.crew(group.members.length, group.seats_total)}
        </h2>
        <ul className="flex flex-col gap-1.5">
          {group.members.map((member) => (
            <li
              key={member.membership_id}
              className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-sm"
            >
              <UserAvatar user={member.user} className="size-8" fallbackClassName="bg-secondary text-xs font-semibold" />
              <span className="font-medium">{member.user.full_name}</span>
              <span className="text-primary text-xs font-semibold">
                {formatDays(member.days)}
              </span>
              {member.phone && (
                <a
                  href={`tel:${member.phone.replace(/\s/g, "")}`}
                  className="text-muted-foreground flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                >
                  <PhoneIcon className="size-3" aria-hidden /> {member.phone}
                </a>
              )}
              {member.joined_at && (
                <span className="text-muted-foreground text-xs">
                  {t.group.ridingSince(formatMonthYear(member.joined_at))}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
      )}

      {canJoin && (
        <Button
          className="h-12 text-base font-semibold"
          onClick={() => {
            // Fresh every time: a cancelled dialog must not reopen with the
            // previous message or with every day unticked and a dead button.
            setJoinDays(null);
            setJoinMessage("");
            setJoinOpen(true);
          }}
        >
          {t.group.join}
        </Button>
      )}
      {isPending && (
        <PendingRequestCard
          group={group}
          action={
            <Button
              variant="outline"
              size="sm"
              disabled={leaveGroup.isPending || !group.my_membership_id}
              onClick={() => {
                if (!group.my_membership_id) return;
                leaveGroup.mutate({ membershipId: group.my_membership_id, wasPending: true });
              }}
            >
              {t.home.withdrawRequest}
            </Button>
          }
        />
      )}
      {!canJoin && !isCrew && !isPending && (
        <p className="text-muted-foreground rounded-xl border border-dashed p-4 text-center text-sm">
          {group.status !== "active"
            ? t.group.paused
            : group.seats_free === 0
              ? t.group.full
              : t.group.noFreeDaysForYou}
        </p>
      )}

      {isCrew && (
        <section className="flex flex-col overflow-hidden rounded-xl border">
          <h2 className="bg-card border-b px-4 py-3 text-sm font-semibold">{t.chat.title}</h2>
          <div className="h-[55dvh] min-h-72">
            <ChatPanel groupId={group.id} />
          </div>
        </section>
      )}

      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.group.joinTitle}</DialogTitle>
            <DialogDescription>{t.group.joinBody(group.driver.full_name)}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label>{t.days.label}</Label>
            <DayPicker
              allowed={joinableDays}
              selected={joinDays ?? joinableDays}
              onChange={(days) => setJoinDays(days)}
              idPrefix="join-day"
            />
            {joinableDays.length < group.days.length && (
              <p className="text-muted-foreground text-xs">{t.group.busyDaysHidden}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="join-message">{t.group.joinMessage}</Label>
            <Textarea
              id="join-message"
              maxLength={300}
              placeholder={t.group.joinMessagePlaceholder}
              value={joinMessage}
              onChange={(event) => setJoinMessage(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJoinOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              disabled={requestJoin.isPending || (joinDays !== null && joinDays.length === 0)}
              onClick={() =>
                requestJoin.mutate(
                  { message: joinMessage.trim() || null, days: joinDays ?? joinableDays },
                  {
                    onSuccess: () => {
                      setJoinOpen(false);
                      void navigate("/");
                    },
                  },
                )
              }
            >
              {t.group.join}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
